import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { carregarRelatorioPortal } from '../lib/portal';
import RelatorioView from './portal/RelatorioView';

function hoje() { return new Date().toISOString().split('T')[0]; }
function diasAtras(n) { return new Date(Date.now() - n * 86400000).toISOString().split('T')[0]; }

const CAMPOS = [
  { key: 'impressoes', label: 'Impressões (tráfego pago)' },
  { key: 'cliques', label: 'Cliques' },
  { key: 'valor_gasto', label: 'Valor investido (R$)' },
  { key: 'seguidores', label: 'Total de seguidores (acumulado)' },
  { key: 'novos_seguidores', label: 'Novos seguidores' },
  { key: 'curtidas', label: 'Curtidas' },
  { key: 'comentarios', label: 'Comentários' },
  { key: 'compartilhamentos', label: 'Compartilhamentos' },
  { key: 'visualizacoes', label: 'Visualizações' },
  { key: 'videos_postados', label: 'Vídeos postados' },
];

const FORM_VAZIO = Object.fromEntries([['data', hoje()], ...CAMPOS.map(c => [c.key, ''])]);

export default function RelatorioCliente() {
  const { projetoId } = useParams();
  const navigate = useNavigate();

  const [projeto, setProjeto] = useState(null);
  const [entradas, setEntradas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);

  const [inicio, setInicio] = useState(diasAtras(15));
  const [fim, setFim] = useState(hoje());
  const [preview, setPreview] = useState(null);
  const [carregandoPreview, setCarregandoPreview] = useState(false);

  useEffect(() => { carregarTudo(); }, [projetoId]);
  useEffect(() => { if (projeto?.portal_token) carregarPreview(); }, [projeto, inicio, fim]);

  async function carregarTudo() {
    setCarregando(true);
    const [{ data: p }, { data: docs }] = await Promise.all([
      supabase.from('projetos').select('id, nome, cor, portal_token').eq('id', projetoId).single(),
      supabase.from('metricas_diarias').select('*').eq('projeto_id', projetoId).order('data', { ascending: false }),
    ]);
    setProjeto(p || null);
    setEntradas(docs || []);
    setCarregando(false);
  }

  async function carregarPreview() {
    setCarregandoPreview(true);
    try {
      const dados = await carregarRelatorioPortal(projeto.portal_token, inicio, fim);
      setPreview(dados);
    } catch (e) {
      setPreview(null);
    }
    setCarregandoPreview(false);
  }

  function abrirEdicao(entrada) {
    setForm({ ...FORM_VAZIO, ...entrada });
  }

  async function salvar() {
    if (!form.data) return;
    setSalvando(true);
    const payload = { projeto_id: projetoId, data: form.data };
    CAMPOS.forEach(c => { payload[c.key] = form[c.key] === '' ? null : Number(form[c.key]); });
    const { data, error } = await supabase.from('metricas_diarias').upsert(payload, { onConflict: 'projeto_id,data' }).select().single();
    if (error) { alert('Erro ao salvar: ' + error.message); setSalvando(false); return; }
    setEntradas(prev => {
      const semEssa = prev.filter(e => e.data !== data.data);
      return [data, ...semEssa].sort((a, b) => b.data.localeCompare(a.data));
    });
    setForm(FORM_VAZIO);
    setSalvando(false);
    carregarPreview();
  }

  async function excluir(entrada) {
    if (!window.confirm(`Excluir os dados de ${entrada.data}?`)) return;
    const { error } = await supabase.from('metricas_diarias').delete().eq('id', entrada.id);
    if (!error) { setEntradas(entradas.filter(e => e.id !== entrada.id)); carregarPreview(); }
  }

  if (carregando) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Carregando...</div>;
  if (!projeto) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Projeto não encontrado.</div>;

  return (
    <div>
      <div className="section-header">
        <button onClick={() => navigate('/projetos')} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)' }}>← Projetos</button>
        <h2 className="section-title">Relatório — {projeto.nome}</h2>
        <div className="section-line" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20, alignItems: 'flex-start' }}>
        {/* Formulário de lançamento */}
        <div className="card" style={{ padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 14, textTransform: 'uppercase' }}>Lançar métricas do dia</div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', marginBottom: 4 }}>Data</label>
            <input type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--bg-secondary)', boxSizing: 'border-box' }} />
          </div>
          {CAMPOS.map(c => (
            <div key={c.key} style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', marginBottom: 4 }}>{c.label}</label>
              <input type="number" value={form[c.key]} onChange={e => setForm({ ...form, [c.key]: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--bg-secondary)', boxSizing: 'border-box' }} />
            </div>
          ))}
          <button onClick={salvar} disabled={salvando}
            style={{ width: '100%', background: 'var(--purple)', color: 'white', border: 'none', padding: 11, borderRadius: 7, fontWeight: 700, cursor: 'pointer', fontSize: 13, marginTop: 6 }}>
            {salvando ? 'Salvando...' : (entradas.some(e => e.data === form.data) ? 'Atualizar este dia' : 'Salvar')}
          </button>

          {entradas.length > 0 && (
            <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase' }}>Dias já lançados</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
                {entradas.map(e => (
                  <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', borderRadius: 6, background: 'var(--bg-secondary)' }}>
                    <button onClick={() => abrirEdicao(e)} style={{ background: 'transparent', border: 'none', color: 'var(--purple-rich)', cursor: 'pointer', fontSize: 12, fontWeight: 600, textAlign: 'left' }}>
                      {new Date(e.data + 'T00:00').toLocaleDateString('pt-BR')}
                    </button>
                    <button onClick={() => excluir(e)} style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11 }}>excluir</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Preview do que o cliente vê */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase' }}>Pré-visualização (igual ao que o cliente vê)</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
            <input type="date" value={inicio} onChange={e => setInicio(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12.5, background: 'var(--bg-secondary)' }} />
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>até</span>
            <input type="date" value={fim} onChange={e => setFim(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12.5, background: 'var(--bg-secondary)' }} />
          </div>
          {carregandoPreview && <div style={{ color: 'var(--gray-400)' }}>Carregando...</div>}
          {!carregandoPreview && preview && <RelatorioView dados={preview} />}
        </div>
      </div>
    </div>
  );
}
